import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper
} from '@mui/material';
import { Close as CloseIcon, AutoAwesome } from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';

interface GenerateMarketImageDialogProps {
  open: boolean;
  onClose: () => void;
  marketId: string;
  marketName: string;
  country: string;
  imageType: 1 | 2 | 3;
  onSuccess: () => void;
}

export const GenerateMarketImageDialog: React.FC<GenerateMarketImageDialogProps> = ({
  open,
  onClose,
  marketId,
  marketName,
  country,
  imageType,
  onSuccess
}) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [prompt, setPrompt] = useState('');
  const [variation, setVariation] = useState<0 | 1 | 2>(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);

  const variations = [
    {
      id: 0,
      label: 'Wide Cityscape View',
      description: 'Stunning panoramic view showcasing iconic city landmarks and urban architecture'
    },
    {
      id: 1,
      label: 'Street-Level Perspective',
      description: 'Dynamic ground-level view capturing vibrant street life and architectural details'
    },
    {
      id: 2,
      label: 'Aerial View',
      description: 'Spectacular elevated perspective showing city layout and geographical features'
    }
  ];

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    setGeneratedImage(null);

    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/marketcompass/admin/markets/${marketId}/generate-image`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            marketName,
            country,
            prompt: prompt.trim() || undefined,
            imageType,
            variation
          })
        }
      );

      if (!response || !response.ok) {
        const errorData = response ? await response.json() : { error: 'No response from server' };
        throw new Error(errorData.error || 'Failed to generate image');
      }

      const result = await response.json();
      const base64 = result.data.imageBase64;

      setImageBase64(base64);
      setGeneratedImage(`data:${result.data.contentType};base64,${base64}`);
    } catch (err: any) {
      console.error('Error generating image:', err);
      setError(err.message || 'Failed to generate image');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!imageBase64) return;

    setIsSaving(true);
    setError(null);

    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/marketcompass/admin/markets/${marketId}/save-generated-image`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageBase64,
            imageType,
            marketName
          })
        }
      );

      if (!response || !response.ok) {
        const errorData = response ? await response.json() : { error: 'No response from server' };
        throw new Error(errorData.error || 'Failed to save image');
      }

      onSuccess();
      handleClose();
    } catch (err: any) {
      console.error('Error saving image:', err);
      setError(err.message || 'Failed to save image');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isGenerating && !isSaving) {
      setPrompt('');
      setVariation(0);
      setGeneratedImage(null);
      setImageBase64(null);
      setError(null);
      onClose();
    }
  };

  const getImageTypeLabel = () => {
    switch (imageType) {
      case 1: return 'Product Image 1 (with template overlay)';
      case 2: return 'Product Image 2';
      case 3: return 'Product Image 3';
      default: return `Product Image ${imageType}`;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { m: 2 }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          fontWeight: 'bold'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesome sx={{ color: '#F5A800' }} />
          Generate New Image
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={isGenerating || isSaving}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Market: <strong>{marketName}, {country}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Image Type: <strong>{getImageTypeLabel()}</strong>
          </Typography>
          {imageType === 1 && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Product Image 1 will include the Horwath HTL template overlay with the market name and country displayed in the bottom left corner.
            </Alert>
          )}
        </Box>

        <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
          <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
            Select Image Style
          </FormLabel>
          <RadioGroup
            value={variation}
            onChange={(e) => setVariation(parseInt(e.target.value) as 0 | 1 | 2)}
          >
            {variations.map((v) => (
              <Paper
                key={v.id}
                elevation={variation === v.id ? 3 : 1}
                sx={{
                  p: 1.5,
                  mb: 1,
                  border: '2px solid',
                  borderColor: variation === v.id ? '#F5A800' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: '#F5A800',
                    elevation: 2
                  }
                }}
                onClick={() => setVariation(v.id as 0 | 1 | 2)}
              >
                <FormControlLabel
                  value={v.id}
                  control={
                    <Radio
                      sx={{
                        color: '#F5A800',
                        '&.Mui-checked': {
                          color: '#F5A800'
                        }
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {v.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {v.description}
                      </Typography>
                    </Box>
                  }
                  sx={{ width: '100%', m: 0 }}
                />
              </Paper>
            ))}
          </RadioGroup>
        </FormControl>

        <TextField
          fullWidth
          label="Additional Prompt Details (Optional)"
          placeholder="Add any specific details you want included in the image..."
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          margin="normal"
          multiline
          rows={3}
          helperText="The selected style will be combined with any additional details you provide here"
          disabled={isGenerating || isSaving}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {isGenerating && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#F5A800' }} size={60} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Generating image... This may take 30-60 seconds.
            </Typography>
          </Box>
        )}

        {generatedImage && !isGenerating && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Preview:
            </Typography>
            <Box
              component="img"
              src={generatedImage}
              alt="Generated preview"
              sx={{
                width: '100%',
                height: 'auto',
                borderRadius: 1,
                border: '2px solid',
                borderColor: '#F5A800'
              }}
            />
            <Alert severity="success" sx={{ mt: 2 }}>
              Image generated successfully! Review the preview above and click "Save & Publish" to replace the existing image.
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isGenerating || isSaving}
          sx={{ color: '#607D8B' }}
        >
          Cancel
        </Button>

        {!generatedImage && (
          <Button
            onClick={handleGenerate}
            variant="contained"
            disabled={isGenerating}
            startIcon={<AutoAwesome />}
            sx={{
              bgcolor: '#F5A800',
              color: '#000000',
              fontWeight: 600,
              '&:hover': { bgcolor: '#D89500' }
            }}
          >
            {isGenerating ? 'Generating...' : 'Generate Image'}
          </Button>
        )}

        {generatedImage && (
          <>
            <Button
              onClick={handleGenerate}
              variant="outlined"
              disabled={isGenerating || isSaving}
              startIcon={<AutoAwesome />}
              sx={{
                color: '#F5A800',
                borderColor: '#F5A800',
                '&:hover': {
                  borderColor: '#D89500',
                  bgcolor: 'rgba(245, 168, 0, 0.04)'
                }
              }}
            >
              Regenerate
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={isSaving}
              sx={{
                bgcolor: '#32373C',
                color: '#FFFFFF',
                fontWeight: 700,
                '&:hover': { bgcolor: '#1f2327' }
              }}
            >
              {isSaving ? 'Saving...' : 'Save & Publish'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
